/**
 * @fileoverview Promotional Cover & Stadium Waiting Room Screen Component for "Esquivador" (Surfer - Id: 51).
 * Renders high-impact arcade branding on stadium LED screens (/screen), featuring dynamic background layers,
 * sponsor/club logos, animated neon light beams, versus presentation badges, and fan controller scan instructions.
 * Implements concurrent RAM preloading via PreloadThemeImages for instantaneous, flicker-free game start.
 * 
 * @module components/Screen/Sections/Surfer/SurferCover
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetThemeById, PreloadThemeImages } from '../../../../utilities/BL/Surfer';
import {
    resolveAssetUrl,
    DEFAULT_BACKGROUND_SVG,
    DEFAULT_PLAYER1_SURFER_SVG,
    DEFAULT_PLAYER2_SURFER_SVG
} from './SurferAssets';

/**
 * Fallback values for visual theme attributes.
 * @constant {Object}
 */
const DEFAULT_COVER_THEME = {
    name: 'ESQUIVADOR 1 VS 1',
    coverTitle: 'ESQUIVADOR 1 VS 1',
    coverBackgroundType: 'image',
    coverBackgroundColor: '#070014',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    primaryColor: '#00ffcc',
    secondaryColor: '#ff007f',
    brandLogoUrl: '',
    sponsorLogoUrl: ''
};

/**
 * SurferCover Component (/screen - Component 51)
 * 
 * @function SurferCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.theme] - Theme object passed from Screen.js router or socket payload.
 * @param {Object} [props.payload] - WebSocket payload fallback.
 * @param {Object} [props.config] - Config object fallback.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const SurferCover = ({ theme: propTheme, payload, config: propConfig, roomId }) => {
    // =========================================================================
    // 1. GESTIÓN DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);
    const [hasPreloaded, setHasPreloaded] = useState(false);

    /**
     * Consulta y normaliza la configuración del tema desde el backend REST si no vino en props.
     */
    const fetchActiveTheme = useCallback(async () => {
        if (!roomId) return;
        try {
            const configRes = await GetConfig(roomId);
            if (configRes && configRes.success && configRes.data) {
                const conf = configRes.data;
                if (conf.activeTheme) {
                    setActiveThemeData(conf.activeTheme);
                } else if (conf.activeThemeId) {
                    const themeRes = await GetThemeById(conf.activeThemeId);
                    if (themeRes && themeRes.success && themeRes.data) {
                        setActiveThemeData(themeRes.data);
                    }
                }
            }
        } catch (err) {
            console.warn('[SurferCover] No se pudo cargar configuración remota del tema:', err);
        }
    }, [roomId]);

    // Sincronización del tema a partir de props o llamada REST
    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme;
            if (confTheme) {
                setActiveThemeData(confTheme);
                return;
            }
        }

        if (payload) {
            const incomingTheme = payload.theme || payload.state?.theme || payload.data?.theme;
            if (incomingTheme) {
                setActiveThemeData(incomingTheme);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveTheme();
        }
    }, [propTheme, propConfig, payload, activeThemeData, fetchActiveTheme]);

    // =========================================================================
    // 2. PRECARGA ASÍNCRONA DE RECURSOS EN MEMORIA RAM
    // =========================================================================
    useEffect(() => {
        if (!activeThemeData || hasPreloaded) return;

        const performPreload = async () => {
            try {
                await PreloadThemeImages(activeThemeData);
                setHasPreloaded(true);
            } catch (preloadErr) {
                console.warn('[SurferCover] Fallo parcial en la precarga de imágenes:', preloadErr);
            }
        };

        performPreload();
    }, [activeThemeData, hasPreloaded]);

    // =========================================================================
    // 3. ATRIBUTOS CONSOLIDADOS Y RESOLUCIÓN MULTIMEDIA
    // =========================================================================
    const resolvedTheme = useMemo(() => {
        return {
            ...DEFAULT_COVER_THEME,
            ...(activeThemeData || {})
        };
    }, [activeThemeData]);

    const titleText = resolvedTheme.coverTitle || resolvedTheme.name || DEFAULT_COVER_THEME.coverTitle;
    const bgType = resolvedTheme.coverBackgroundType || 'image';
    const bgUrl = resolveAssetUrl(resolvedTheme.backgroundUrl || resolvedTheme.coverBackgroundUrl);
    const brandLogo = resolveAssetUrl(resolvedTheme.brandLogoUrl, '/img/page/xolos-five.png');
    const sponsorLogo = resolveAssetUrl(resolvedTheme.sponsorLogoUrl);
    const p1Color = resolvedTheme.primaryColor || DEFAULT_COVER_THEME.primaryColor;
    const p2Color = resolvedTheme.secondaryColor || DEFAULT_COVER_THEME.secondaryColor;

    // =========================================================================
    // 4. CAPA DE FONDO DINÁMICO (ZERO-CANVAS GPU OPTIMIZED)
    // =========================================================================
    const renderBackground = () => {
        if (bgType === 'video' && bgUrl) {
            return (
                <video
                    src={bgUrl}
                    className="surfer-cover__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        const effectiveBg = bgUrl || DEFAULT_BACKGROUND_SVG;
        return (
            <div
                className="surfer-cover__background-image"
                style={{ backgroundImage: `url(${effectiveBg})` }}
            />
        );
    };

    return (
        <main
            className="surfer-cover"
            role="region"
            aria-label="Portada Estadio Esquivador 1 vs 1"
        >
            {/* Fondo dinámico renderizado con aceleración por hardware */}
            {renderBackground()}

            {/* Capa de Viñeta y Filtro de Contraste */}
            <div className="surfer-cover__vignette" />

            {/* Haces de luz de estadio animados */}
            <div className="surfer-cover__beam surfer-cover__beam--left" />
            <div className="surfer-cover__beam surfer-cover__beam--right" />

            {/* Resplandor neón inferior de pista */}
            <div className="surfer-cover__ground-glow" />

            {/* Contenido Principal Centrado */}
            <div className="surfer-cover__content">
                {/* Contenedor Superior de Logotipos */}
                <header className="surfer-cover__logos-wrapper">
                    {sponsorLogo && (
                        <div className="surfer-cover__logo-box surfer-cover__logo-box--sponsor">
                            <img
                                src={sponsorLogo}
                                alt="Patrocinador Oficial"
                                className="surfer-cover__logo"
                            />
                        </div>
                    )}
                    <div className="surfer-cover__logo-box surfer-cover__logo-box--brand">
                        <img
                            src={brandLogo}
                            alt="Marca / Club"
                            className="surfer-cover__logo"
                        />
                    </div>
                </header>

                {/* Badge Presenta */}
                <div className="surfer-cover__presenta-badge">
                    <span>PRESENTA</span>
                </div>

                {/* Título Principal Arcade */}
                <h1 className="surfer-cover__hero-title">
                    <span className="surfer-cover__hero-title-main">{titleText}</span>
                    <span className="surfer-cover__hero-title-glow" aria-hidden="true">{titleText}</span>
                </h1>

                {/* Subtítulo de Desafío */}
                <p className="surfer-cover__hero-subtitle">
                    ¡ESQUIVA OBSTÁCULOS, RECOLECTA MONEDAS Y SUPERA A TU RIVAL EN PISTA!
                </p>

                {/* Escenario de Presentación 1 vs 1 */}
                <section className="surfer-cover__vs-stage">
                    {/* Bando Jugador 1 */}
                    <div className="surfer-cover__player-card surfer-cover__player-card--p1" style={{ '--player-color': p1Color }}>
                        <div className="surfer-cover__player-vehicle">
                            <img
                                src={resolveAssetUrl(resolvedTheme.player1Url, DEFAULT_PLAYER1_SURFER_SVG)}
                                alt="Nave Jugador 1"
                                className="surfer-cover__vehicle-img"
                            />
                        </div>
                        <div className="surfer-cover__player-info">
                            <span className="surfer-cover__player-tag" style={{ color: p1Color }}>LADO IZQUIERDO</span>
                            <h2 className="surfer-cover__player-title" style={{ color: p1Color }}>JUGADOR 1</h2>
                        </div>
                    </div>

                    {/* Insignia Central VS con Efecto Pulso */}
                    <div className="surfer-cover__vs-badge">
                        <div className="surfer-cover__vs-badge-inner">
                            <span className="surfer-cover__vs-text">VS</span>
                        </div>
                        <div className="surfer-cover__vs-pulse" />
                    </div>

                    {/* Bando Jugador 2 */}
                    <div className="surfer-cover__player-card surfer-cover__player-card--p2" style={{ '--player-color': p2Color }}>
                        <div className="surfer-cover__player-vehicle">
                            <img
                                src={resolveAssetUrl(resolvedTheme.player2Url, DEFAULT_PLAYER2_SURFER_SVG)}
                                alt="Nave Jugador 2"
                                className="surfer-cover__vehicle-img"
                            />
                        </div>
                        <div className="surfer-cover__player-info">
                            <span className="surfer-cover__player-tag" style={{ color: p2Color }}>LADO DERECHO</span>
                            <h2 className="surfer-cover__player-title" style={{ color: p2Color }}>JUGADOR 2</h2>
                        </div>
                    </div>
                </section>

                {/* Llamado a la Afición a Escanear Mando */}
                <aside className="surfer-cover__qr-callout">
                    <div className="surfer-cover__qr-callout-icon">
                        <i className="bi bi-phone-vibrate-fill" />
                    </div>
                    <div className="surfer-cover__qr-callout-text">
                        <h3 className="surfer-cover__qr-callout-heading">¡ESCANEA EL CÓDIGO QR EN PANTALLA CON TU MÓVIL!</h3>
                        <p className="surfer-cover__qr-callout-sub">Elige tu lado en el mando y prepárate para esquivar los obstáculos a máxima velocidad.</p>
                    </div>
                </aside>
            </div>

            {/* Pie de Página Corporativo */}
            <footer className="surfer-cover__footer">
                <span className="surfer-cover__footer-brand">Hecho con ♥ por Interaktia</span>
                <span className="surfer-cover__footer-badge">DINÁMICA 51 • 60 FPS ULTRA SCREEN</span>
            </footer>
        </main>
    );
};

SurferCover.propTypes = {
    theme: PropTypes.object,
    payload: PropTypes.object,
    config: PropTypes.object,
    roomId: PropTypes.string
};

SurferCover.defaultProps = {
    theme: null,
    payload: null,
    config: null,
    roomId: null
};

export default SurferCover;
